import * as React from "react";
import { classes, style } from "typestyle";
import { html, i18nKeys, t } from "../../i18n/i18n";
import { Throbber } from "../Throbber";
import { getLoginService } from "../../login-service/LoginService";
import { LoginServiceError } from "../../login-service/LoginServiceErrors";
import { getParameterFromQueryString } from "../../querystring-util";
import redirectToApp from "../redirect-to-app";
import trackAction from "../../libs/analytics/SigninPageAnalytics";
import { BigLogo } from "../BigLogo";
import {
  Alert,
  Button,
  PasswordStrengthMeter,
  TextItem,
  Window,
  WindowBody,
  WindowVerticalLayout
} from "@traceair/tds";
import PasswordInput from "../forms/PasswordInput";
import {
  boldBigStyle,
  descriptionStyle,
  greetingStyle,
  headerStyle,
  windowStyle
} from "./styles";
import {
  assessPassword,
  PasswordAssessment
} from "@traceair/fordevind-password-policy";
import { AlertIcon } from "@traceair/webapp-icons";

interface ISetNewPasswordPageProps {
  refererUrl: string;
}

interface ISetNewPasswordPageState {
  pending: boolean;
  error?: LoginServiceError;
  password: string;
  passwordWarning?: string;
  confirmedPassword: string;
  confirmedPasswordWarning: string;

  assessment: PasswordAssessment;
  showMismatchAlert: boolean;
}

export class SetNewPasswordPage extends React.Component<
  ISetNewPasswordPageProps,
  ISetNewPasswordPageState
> {
  constructor(props: ISetNewPasswordPageProps) {
    super(props);
    this.state = {
      pending: false,
      password: "",
      passwordWarning: "",
      confirmedPassword: "",
      confirmedPasswordWarning: "",
      error: undefined,
      assessment: assessPassword(""),
      showMismatchAlert: false
    };
    trackAction("Set new password page is opened");
  }

  validatePassword = (password: string) => {
    this.setState({ passwordWarning: "" });
    if (!password) {
      this.setState({ passwordWarning: t(i18nKeys.passwordRequired) });
      return false;
    }
    const assessment = assessPassword(password);
    this.setState({ assessment });

    if (!assessment.canSave) {
      this.setState({
        passwordWarning: assessment.feedbackText || t(i18nKeys.weakPassword)
      });
      return false;
    }

    return true;
  };

  validateConfirmedPassword = (confirmedPassword: string, password: string) => {
    this.setState({ confirmedPasswordWarning: "" });
    if (password !== confirmedPassword) {
      this.setState({
        confirmedPasswordWarning: t(i18nKeys.passwordsDoNotMatchTitle)
      });
      return false;
    }

    return true;
  };

  handleSubmit = (e: React.SyntheticEvent) => {
    e.preventDefault();
    this.setState({ pending: true, showMismatchAlert: true });

    const { password, confirmedPassword } = this.state;
    const isPasswordCorrect = this.validatePassword(password);
    const isConfirmedPasswordCorrect = this.validateConfirmedPassword(
      confirmedPassword,
      password
    );

    if (!isPasswordCorrect || !isConfirmedPasswordCorrect) {
      this.setState({ pending: false });
      return;
    }

    const token = getPasswordTokenFromQuerystring();

    getLoginService()
      .setNewPassword(password, token)
      .catch((error: LoginServiceError) => {
        this.setState({ error, pending: false });
        throw error;
      })
      .then(() => {
        this.navigateToApp();
      });
  };

  navigateToApp = () => {
    redirectToApp(this.props.refererUrl);
  };

  render() {
    const { pending, assessment } = this.state;
    return (
      <Window className={windowStyle} header={this.renderHeader()}>
        <WindowBody>
          <form onSubmit={this.handleSubmit}>
            <Throbber pending={pending}>
              <WindowVerticalLayout>
                {this.renderPasswordsMismatchAlert()}
                {this.renderPasswordInput()}
                <div className={passwordAssessmentContainerStyle}>
                  <PasswordStrengthMeter
                    score={assessment.score}
                    checklist={assessment.checklist}
                    feedbackText={assessment.feedbackText}
                  />
                </div>

                {this.renderConfirmedPasswordInput()}
              </WindowVerticalLayout>
            </Throbber>
            {this.renderFooter()}
          </form>
        </WindowBody>
      </Window>
    );
  }

  renderHeader = () => {
    return (
      <div className={headerStyle}>
        <BigLogo />
        <TextItem
          text={t(i18nKeys.changePasswordGreetingTitle)}
          style={classes(boldBigStyle, greetingStyle)}
        />
        <div className={descriptionStyle}>
          {html(t(i18nKeys.changePasswordGreetingDiscription))}
        </div>
      </div>
    );
  };

  renderPasswordsMismatchAlert = () => {
    const { password, confirmedPassword, showMismatchAlert } = this.state;

    if (!showMismatchAlert) {
      return null;
    }

    const shouldShow =
      password.length > 0 &&
      confirmedPassword.length > 0 &&
      password !== confirmedPassword;

    if (!shouldShow) {
      return null;
    }

    return (
      <Alert
        type="warning"
        title={t(i18nKeys.passwordsDoNotMatchTitle)}
        description={t(i18nKeys.passwordsDoNotMatchSubtitle)}
        icon={AlertIcon}
      />
    );
  };

  renderPasswordInput = () => {
    const { password, passwordWarning } = this.state;
    const onChange = (password: string) => {
      const assessment = assessPassword(password);
      this.setState({ password, assessment, passwordWarning: "" });
    };
    return (
      <PasswordInput
        title={t(i18nKeys.setANewPassword)}
        placeholder={t(i18nKeys.nowSetANewPassword)}
        value={password}
        onChange={onChange}
        isWarning={!!passwordWarning}
        warningMessage={passwordWarning}
        onBlur={() => this.validatePassword(password)}
      />
    );
  };

  renderConfirmedPasswordInput = () => {
    const { password, confirmedPassword } = this.state;
    const onChange = (newConfirmedPassword: string) => {
      this.setState({ confirmedPassword: newConfirmedPassword });
    };
    return (
      <PasswordInput
        title={t(i18nKeys.confirmPassword)}
        placeholder={t(i18nKeys.confirmPassword)}
        value={confirmedPassword}
        onChange={onChange}
        onBlur={() =>
          this.validateConfirmedPassword(confirmedPassword, password)
        }
      />
    );
  };

  renderFooter = () => {
    const { assessment, pending, password, confirmedPassword } = this.state;

    const passwordsMatch = password.length > 0 && confirmedPassword.length > 0;

    return (
      <div className={wrapperStyle}>
        <Button
          text={t(i18nKeys.setPasswordAndSignIn)}
          style="primary"
          htmlType="submit"
          disabled={!assessment.canSave || !passwordsMatch || pending}
          className={setPasswordButtonStyle}
        />
      </div>
    );
  };
}

function getPasswordTokenFromQuerystring() {
  return getParameterFromQueryString(window.location.href, "passwordToken");
}

const passwordAssessmentContainerStyle = style({
  paddingTop: 4,
  paddingBottom: 4
});

const setPasswordButtonStyle = style({
  width: 160
});

const wrapperStyle = style({
  display: "flex",
  width: "100%",
  justifyContent: "end",
  padding: 12
});
